/**
 * Inline SVG monthly bar chart. No chart library — keeps @kilat/ui
 * dep-free and predictable.
 *
 * Sprint 94 — used by the agent + recruiter dashboards to render
 * trailing-6-month earnings. Recurring + bonus split visible per bar
 * so the agent can see "recurring is growing" vs "bonus spike this
 * month from new signup".
 *
 * Pure visual — takes already-bucketed data, no fetching or math.
 * Caller is expected to pass `summariseLedgerByMonth(entries, nowMs)`.
 *
 * Honest design notes:
 *   - Heights derived from the actual max, so a small earner's chart
 *     scales the same as a big earner's — never compress a small bar
 *     to "zero" relative to a normal one.
 *   - Empty months render as a faint baseline mark, not invisible —
 *     "I earned nothing in May" is a meaningful signal, not absence
 *     of data.
 *   - Stacks render recurring on bottom (foundation) and bonus on
 *     top (volatile) so the eye reads "this is the steady part".
 */

export interface MonthlyBarChartBucket {
  readonly monthKey: string;
  readonly label: string;
  readonly recurringSen: number;
  readonly bonusSen: number;
  /** Optional clawback magnitude (positive number, will render as red overlay). */
  readonly clawbackSen?: number;
}

export interface MonthlyBarChartProps {
  readonly buckets: readonly MonthlyBarChartBucket[];
  /** Formatter — caller supplies so the chart respects locale. */
  readonly formatMoney: (sen: number) => string;
  /** Optional: highlight the most-recent COMPLETED month with a label. */
  readonly highlightLatest?: boolean;
  /** Tone — `recruiter` flips bar colors to differentiate from agent. */
  readonly tone?: 'agent' | 'recruiter';
  /** Empty-state copy. Shared component, so the caller supplies the words. */
  readonly emptyLabel: string;
  readonly emptyBody: string;
}

export function MonthlyBarChart(props: MonthlyBarChartProps) {
  const {
    buckets,
    formatMoney,
    highlightLatest = true,
    tone = 'agent',
    emptyLabel,
    emptyBody,
  } = props;

  if (buckets.length === 0) {
    return (
      <div
        role="img"
        aria-label={emptyLabel}
        style={{
          padding: '1rem',
          fontSize: 'var(--text-meta, 0.8125rem)',
          color: 'var(--color-on-surface-muted, #64748b)',
          textAlign: 'center',
        }}
      >
        {emptyBody}
      </div>
    );
  }

  const max = Math.max(1, ...buckets.map((b) => Math.max(0, b.recurringSen + b.bonusSen)));
  const recurringFill = tone === 'recruiter' ? '#475569' : '#475569';
  // Semantic amber/green for the two series, not the brand hue — this chart
  // lives in a shared package and must not encode one platform's palette.
  const bonusFill = tone === 'recruiter' ? '#f59e0b' : '#16a34a';
  const clawbackFill = '#ef4444';

  const padTop = 24;
  const padBottom = 36;
  const padX = 12;
  const chartHeight = 160;
  const barWidth = 36;
  const barGap = 18;
  const totalWidth = buckets.length * barWidth + (buckets.length - 1) * barGap + padX * 2;
  const innerHeight = chartHeight - padTop - padBottom;

  return (
    <div
      role="img"
      aria-label="Last six months of earnings"
      style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
    >
      <svg
        viewBox={`0 0 ${totalWidth} ${chartHeight}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          display: 'block',
          width: '100%',
          maxWidth: '36rem',
          height: 'auto',
          margin: '0 auto',
        }}
      >
        {/* Baseline */}
        <line
          x1={padX}
          x2={totalWidth - padX}
          y1={chartHeight - padBottom}
          y2={chartHeight - padBottom}
          stroke="var(--color-border)"
          strokeWidth={1}
        />
        {buckets.map((b, i) => {
          const x = padX + i * (barWidth + barGap);
          const total = b.recurringSen + b.bonusSen;
          const isLatest = highlightLatest && i === buckets.length - 1;
          const baseY = chartHeight - padBottom;
          const recurringHeight = total > 0 ? (b.recurringSen / max) * innerHeight : 0;
          const bonusHeight = total > 0 ? (b.bonusSen / max) * innerHeight : 0;
          const clawbackHeight =
            b.clawbackSen && b.clawbackSen > 0 ? (b.clawbackSen / max) * innerHeight : 0;

          return (
            <g key={b.monthKey}>
              {/* Empty-month marker so a flat chart still reads as data */}
              {total === 0 ? (
                <line
                  x1={x}
                  x2={x + barWidth}
                  y1={baseY - 1}
                  y2={baseY - 1}
                  stroke="var(--color-border-strong)"
                  strokeWidth={2}
                />
              ) : null}

              {/* Recurring (bottom of stack) */}
              {b.recurringSen > 0 ? (
                <rect
                  x={x}
                  y={baseY - recurringHeight}
                  width={barWidth}
                  height={recurringHeight}
                  rx={4}
                  ry={4}
                  fill={recurringFill}
                  opacity={isLatest ? 1 : 0.85}
                />
              ) : null}

              {/* Bonus (top of stack) */}
              {b.bonusSen > 0 ? (
                <rect
                  x={x}
                  y={baseY - recurringHeight - bonusHeight}
                  width={barWidth}
                  height={bonusHeight}
                  rx={4}
                  ry={4}
                  fill={bonusFill}
                  opacity={isLatest ? 1 : 0.85}
                />
              ) : null}

              {/* Clawback overlay (right edge of bar) */}
              {clawbackHeight > 0 ? (
                <rect
                  x={x + barWidth - 6}
                  y={baseY - clawbackHeight}
                  width={6}
                  height={clawbackHeight}
                  fill={clawbackFill}
                  opacity={0.7}
                />
              ) : null}

              {/* Month label */}
              <text
                x={x + barWidth / 2}
                y={chartHeight - padBottom + 16}
                textAnchor="middle"
                style={{
                  fontSize: 10,
                  fontFamily: 'inherit',
                  fill: isLatest
                    ? 'var(--color-on-surface, #0f172a)'
                    : 'var(--color-on-surface-muted, #64748b)',
                  fontWeight: isLatest ? 600 : 400,
                }}
              >
                {b.label.split(' ')[0]}
              </text>

              {/* Inline amount label for the latest bar */}
              {isLatest && total > 0 ? (
                <text
                  x={x + barWidth / 2}
                  y={baseY - recurringHeight - bonusHeight - 6}
                  textAnchor="middle"
                  style={{
                    fontSize: 11,
                    fontFamily: 'inherit',
                    fill: 'var(--color-on-surface, #0f172a)',
                    fontWeight: 600,
                  }}
                >
                  {formatMoney(total)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div
        style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          marginTop: '0.5rem',
          fontSize: 'var(--text-meta, 0.8125rem)',
          color: 'var(--color-on-surface-muted, #64748b)',
          flexWrap: 'wrap',
        }}
      >
        <LegendDot color={recurringFill} label="Recurring" />
        <LegendDot color={bonusFill} label="Signup bonus" />
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { readonly color: string; readonly label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        aria-hidden
        style={{
          width: 10,
          height: 10,
          borderRadius: 2,
          background: color,
          display: 'inline-block',
        }}
      />
      {label}
    </span>
  );
}
